import {
  faChevronDown,
  faChevronUp,
  faTimes,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";

import { species } from "./utils";
import {
  useMinDurationLoading,
  useWindowDimensions,
} from "../../hooks";
import Twemoji from "../../Twemoji";
import Loading from "../common/loading/Loading";

function EmptyState({ showFailedSearchState, showEmptyState }) {
  return (
    <div className="bg-primary" data-testid="emptyPatients">
      {showFailedSearchState && !showEmptyState ? (
        <h4 className="text-secondary font-normal text-center p-8">
          We couldn&apos;t find a patient with that name in your
          notebook.
          <Twemoji emoji="😿" /> <br /> If you would like to add a new
          patient, use the &quot;Add Patient&quot; button!
        </h4>
      ) : (
        <h4 className="text-secondary font-normal text-center p-8">
          You have no patients in your notebook.{" "}
          <Twemoji emoji="😿" /> <br /> Please add some using the
          &quot;Add Patient&quot; button!
        </h4>
      )}
    </div>
  );
}

export function PatientSearchBar({
  onPatientClick,
  resultsMaxHeight,
  isForPatientSelectionAction,
  setSelectedPatientName,
  setSelectedPatientUuid,
  itemsAsObjects,
  loadingTrigger,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const { width } = useWindowDimensions();

  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const optionRefs = useRef([]);

  const itemToString = (item) => {
    if (item) {
      if (item?.lastNameOfOwner?.length > 0) {
        return `${item.name} ${item.lastNameOfOwner}`;
      } else {
        return item.name;
      }
    } else {
      return "";
    }
  };

  // Derived from props + inputValue
  const filteredItems =
    itemsAsObjects?.filter((item) =>
      itemToString(item)
        .toLowerCase()
        .includes(inputValue.toLowerCase()),
    ) ?? [];
  const showLoading = useMinDurationLoading(loadingTrigger);
  const isLoaded = !loadingTrigger && itemsAsObjects != null;
  const showEmptyState = isLoaded && itemsAsObjects?.length === 0;
  const showFailedSearchState =
    isLoaded &&
    !showEmptyState &&
    filteredItems.length === 0 &&
    inputValue !== "";

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (
      highlightedIndex >= 0 &&
      optionRefs.current[highlightedIndex]
    ) {
      optionRefs.current[highlightedIndex]?.scrollIntoView({
        block: "nearest",
      });
    }
  }, [highlightedIndex]);

  function selectItem(item) {
    setInputValue(itemToString(item));
    setIsOpen(false);
    setHighlightedIndex(-1);

    if (!isForPatientSelectionAction) {
      onPatientClick(item?.uuid);
    } else {
      setSelectedPatientUuid(item?.uuid);
      setSelectedPatientName(item?.name);
    }
  }

  function handleClearInputClick() {
    setInputValue("");
    setHighlightedIndex(-1);
    inputRef.current?.focus();
  }

  function handleKeyDown(event) {
    if (!isOpen) {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredItems.length - 1 ? prev + 1 : prev,
        );
        break;
      case "ArrowUp":
        event.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case "Enter":
        event.preventDefault();
        if (
          highlightedIndex >= 0 &&
          highlightedIndex < filteredItems.length
        ) {
          selectItem(filteredItems[highlightedIndex]);
        }
        break;
      case "Escape":
        event.preventDefault();
        setIsOpen(false);
        break;
      default:
        break;
    }
  }

  return (
    <div ref={containerRef}>
      <div
        className="relative w-full"
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <input
          ref={inputRef}
          autoFocus={width > 500}
          value={inputValue}
          placeholder="Search for a patient here..."
          name="patient"
          onChange={(e) => {
            setInputValue(e.target.value);
            setHighlightedIndex(-1);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          aria-autocomplete="list"
          className="appearance-none w-full bg-input text-primary placeholder:text-placeholder rounded-xl py-3 px-4 pr-12 lg:text-xl shadow-[0px_0px_10px_0px_var(--shadow-primary)] focus:shadow-[0px_0px_10px_0px_var(--shadow-focus)] outline-hidden cursor-text"
        />
        {inputValue ? (
          <button
            type="button"
            className="absolute right-4 top-1/2 -translate-y-1/2 p-1 focus:outline-hidden"
            onClick={handleClearInputClick}
            aria-label="clear selection"
          >
            <FontAwesomeIcon
              icon={faTimes}
              className="text-secondary text-sm"
            />
          </button>
        ) : (
          <button
            type="button"
            className="absolute right-4 top-1/2 -translate-y-1/2 p-1 focus:outline-hidden"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="toggle menu"
          >
            <FontAwesomeIcon
              icon={isOpen ? faChevronUp : faChevronDown}
              className="text-secondary text-xs"
            />
          </button>
        )}
      </div>
      <div className="relative z-10 w-full">
        <div
          role="listbox"
          className={`absolute w-full mt-2 text-sm text-primary bg-input rounded-standard shadow-[0px_0px_10px_0px_var(--shadow-primary)] overflow-y-auto overflow-x-hidden cursor-pointer transition-all duration-200 ${
            isOpen
              ? "opacity-100 translate-y-0"
              : "opacity-0 -translate-y-2 pointer-events-none"
          } ${resultsMaxHeight || "max-h-96"}`}
        >
          {isOpen && showLoading ? (
            <Loading shouldShow={true} />
          ) : isOpen && (showEmptyState || showFailedSearchState) ? (
            <EmptyState
              showFailedSearchState={showFailedSearchState}
              showEmptyState={showEmptyState}
            />
          ) : (
            filteredItems?.map((item, index) => (
              <div
                role="option"
                aria-selected={highlightedIndex === index}
                ref={(el) => {
                  optionRefs.current[index] = el;
                }}
                className={`py-2 px-3 transition-colors ${
                  highlightedIndex === index
                    ? "bg-secondary"
                    : "bg-input hover:bg-secondary"
                }`}
                key={item?.uuid ?? index}
                id={item?.uuid}
                onClick={() => selectItem(item)}
              >
                <div className="flex flex-row items-center">
                  <div className="flex-1 flex-row">
                    <div
                      className={
                        item?.sex == "m"
                          ? "text-blue-400 font-medium text-base"
                          : item?.sex == "f"
                            ? "text-pink-400 font-medium text-base"
                            : "text-indigo-400 font-medium text-base"
                      }
                    >
                      {item?.name} {item?.lastNameOfOwner}
                    </div>
                    <h5 className="text-xs text-secondary">
                      {item?.breed ? `${item.breed}, ` : ""}
                      {item?.age ? item.age : "Born today 🎉"}
                    </h5>
                  </div>
                  <h3>
                    <Twemoji
                      emoji={
                        species[item?.species]?.icon
                          ? species[item?.species]?.icon
                          : "🦄"
                      }
                    />
                  </h3>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
